import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { AlertApi } from '../../../apis/AlertApi/AlertApi';
import { QueryKeys } from '../../../queryDefaults/QueryKeys';
import { AppMedError } from '../../../error/AppMedError';

function sanitizePart(input: string): string {
    return input.replace(/[/\\:*?"<>|]/g, '').replace(/\s+/g, '-');
}

function buildPdfFilename(params: {
    patientName?: string | null;
    patientDateOfBirth?: string | null;
    status?: string;
}): string {
    const nameParts = (params.patientName || 'Unknown').trim().split(/\s+/);
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0];
    const firstName = nameParts.length > 1 ? nameParts.slice(0, -1).join('-') : 'Unknown';
    const dob = params.patientDateOfBirth ? params.patientDateOfBirth.split('T')[0] : 'Unknown';
    const pdfDate = new Date().toISOString().split('T')[0];
    const status = params.status || 'Unknown';

    const raw = `Alert_${sanitizePart(lastName)}_${sanitizePart(firstName)}_${dob}_${pdfDate}_${status}.pdf`;
    return raw.length > 200 ? raw.substring(0, 196) + '.pdf' : raw;
}

interface DownloadAlertPdfParams {
    id: number;
    language: string;
    patientName?: string | null;
    patientDateOfBirth?: string | null;
    status?: string;
}

export function useDownloadAlertPdf() {
    const { t } = useTranslation();

    return useMutation<Blob, AppMedError, DownloadAlertPdfParams>({
        mutationKey: [QueryKeys.alerts, 'downloadPdf'],
        mutationFn: async ({ id, language }) => {
            return await AlertApi.downloadAlertPdf(id, language);
        },
        onSuccess: (blob, params) => {
            const filename = buildPdfFilename(params);
            AlertApi.downloadPdfFile(blob, filename);

            notifications.show({
                title: t('alert.notifications.downloadPdfSuccess'),
                message: t('alert.notifications.downloadPdfSuccessMessage'),
                color: 'green',
            });
        },
        onError: (error: AppMedError) => {
            notifications.show({
                title: t('alert.notifications.downloadPdfError'),
                message: error?.message || t('alert.notifications.downloadPdfErrorMessage'),
                color: 'red',
            });
        },
    });
}
